/**
 * FuzzySearcher - A wrapper around Fuse.js providing enhanced search capabilities.
 * 
 * Features:
 * - Automatic tokenization of search queries
 * - Multi-token search with merged results
 * - Prioritization of exact and prefix matches via reweighting
 * - Built-in defaults that can be overridden
 * 
 * @example
 * const searcher = new FuzzySearcher(items, {
 *   keys: [{ name: 'Name', weight: 0.5 }, { name: 'Code', weight: 0.5 }]
 * });
 * const results = searcher.search('john doe');
 */

import Fuse from "fuse.js";

import {
	FUSE_CORE_DEFAULTS, 
	TOKEN_SEPARATOR 
} from "./options";

export class FuzzySearcher {
	/** @type {Fuse} */
	#fuse;
	
	/** @type {Object} */
	#options;

	/**
	 * Creates a new FuzzySearcher instance.
	 * 
	 * @param {Object[]} [collection=[]] - Initial collection of items to search
	 * @param {Object} [options={}] - Fuse.js options (will be merged with FUSE_CORE_DEFAULTS)
	 */
	constructor(collection = [], options = {}) {
		this.#options = {
			...FUSE_CORE_DEFAULTS,
			...options
		};

		this.#fuse = new Fuse(collection, this.#options);
	}

	/**
	 * Gets the current collection.
	 * @returns {Object[]} The current collection
	 */
	get collection() {
		return this.#fuse.getIndex().docs;
	}

	/**
	 * Sets a new collection of items to search.
	 * @param {Object[]} collection - The new collection
	 */
	setCollection(collection) {
		this.#fuse.setCollection(collection);
	}

	/**
	 * Tokenizes a search query string into individual search terms.
	 * @param {string} query - The search query to tokenize
	 * @returns {string[]} Array of non-empty tokens
	 */
	tokenize(query) {
		if(!query || typeof query !== "string") {
			return [];
		}
		
		const separateTokens = query.trim()
			.split(TOKEN_SEPARATOR)
			.filter(Boolean);

		return [...separateTokens, query.trim()];
	}

	/**
	 * Performs a search with automatic multi-token handling.
	 * 
	 * When a query contains multiple tokens (e.g., "john doe"), this method
	 * searches for each token separately and merges results. Items matching
	 * more tokens will be prioritized by the reweighting logic.
	 * 
	 * @param {string} query - The search query
	 * @param {Object} [options={}] - Search options
	 * @param {boolean} [options.exactOnly=false] - When true, only return items with exact matches
	 * @param {boolean} [options.tokenizeInput=false] - When true, the input query will be tokenized before searching (default is false since search() already handles tokenization)
	 * @param {Function} [options.logger] - Debug logger function
	 * @returns {Object[]} Array of matching items, prioritized by match quality
	 */
	search(query, options = {}) {
		const tokens = options.tokenizeInput !== false ? this.tokenize(query) : [query];
		
		if(!tokens || tokens.length === 0) {
			return [];
		}

		if(options.logger) {
			options.logger("FuzzySearcher.search - tokens:", tokens);
		}

		// Single token - use standard search path
		if(tokens.length === 1) {
			const fuseResults = this.#fuse.search(tokens[0]);
			return this.#reweightResults(tokens, fuseResults, options);
		}

		// Multiple tokens - search each separately and merge results
		const resultsByItem = new Map();

		for(const token of tokens) {
			const tokenResults = this.#fuse.search(token);

			for(const result of tokenResults) {
				const itemKey = result.item.Id ?? result.item.Code ?? JSON.stringify(result.item);

				if(resultsByItem.has(itemKey)) {
					const existing = resultsByItem.get(itemKey);
					existing.matchedTokens.push(token);
					existing.bestScore = Math.min(existing.bestScore, result.score ?? 0);
				} else {
					resultsByItem.set(itemKey, {
						item: result.item,
						bestScore: result.score ?? 0,
						matchedTokens: [token]
					});
				}
			}
		}

		// Convert to fuse result format for reweighting
		const mergedResults = Array.from(resultsByItem.values()).map(r => ({
			item: r.item,
			score: r.bestScore
		}));

		if(options.logger) {
			options.logger("FuzzySearcher.search - merged results:", mergedResults.length);
			options.logger("FuzzySearcher.search - items matching all tokens:",
				Array.from(resultsByItem.values()).filter(r => r.matchedTokens.length === tokens.length).length
			);
		}

		return this.#reweightResults(tokens, mergedResults, options);
	}

	/**
	 * Performs a raw Fuse.js search without multi-token handling or reweighting.
	 * Useful when you need direct access to Fuse.js results.
	 * 
	 * @param {string} query - The search query
	 * @returns {Array} Raw Fuse.js results with {item, score, ...}
	 */
	searchRaw(query) {
		return this.#fuse.search(query);
	}

	/**
	 * Re-weights Fuse.js search results to prioritize exact and prefix matches.
	 * @param {string[]} searchTokens - The search tokens
	 * @param {Array} fuseResults - Raw Fuse.js results
	 * @param {Object} options - Search options
	 * @returns {Object[]} Sorted array of items with $match metadata
	 */
	#reweightResults(searchTokens, fuseResults, options = {}) {
		const { logger, exactOnly } = options;

		if(!searchTokens || searchTokens.length === 0 || !fuseResults || fuseResults.length === 0) {
			return fuseResults?.map(r => r.item) || [];
		}

		if(logger) {
			logger("Reweighting with tokens:", searchTokens);
			logger("Input results count:", fuseResults.length);
			if(exactOnly) {
				logger("Exact only mode enabled");
			}
		}

		let weighted = fuseResults.map(result => {
			const scoreResult = this.#calculateWeightedScore(
				result.item,
				searchTokens,
				result.score ?? 0
			);

			return {
				item: result.item,
				score: scoreResult.score,
				exactCount: scoreResult.exactCount,
				startsWithCount: scoreResult.startsWithCount,
				originalScore: result.score ?? 0
			};
		});

		// When exactOnly is true, filter to only items with at least one exact match
		if(exactOnly) {
			weighted = weighted.filter(w => w.exactCount > 0);
		}

		// Sort by priority: exact count desc, startsWith count desc, then score asc
		weighted.sort((a, b) => {
			if(a.exactCount !== b.exactCount) {
				return b.exactCount - a.exactCount;
			}
			if(a.startsWithCount !== b.startsWithCount) {
				return b.startsWithCount - a.startsWithCount;
			}
			return a.score - b.score;
		});

		if(logger) {
			logger("Reweighted top 5:", weighted.slice(0, 5).map(w => ({
				code: w.item.Code,
				name: w.item.Name,
				exact: w.exactCount,
				startsWith: w.startsWithCount,
				score: w.score.toFixed(4),
				original: w.originalScore.toFixed(4)
			})));
		}

		// Assign rank (higher = better match) and add $match metadata
		const totalResults = weighted.length;
		return weighted.map((w, index) => ({
			...w.item,
			$match: {
				rank: totalResults - index,
				score: {
					original: w.originalScore,
					adjusted: w.score
				},
				hasExact: w.exactCount > 0,
				count: {
					exact: w.exactCount,
					startsWith: w.startsWithCount
				}
			}
		}));
	}

	/**
	 * Calculates a weighted score for an item based on search tokens.
	 * @param {Object} item - The item to score
	 * @param {string[]} searchTokens - The search tokens
	 * @param {number} baseFuzzyScore - The base Fuse.js score
	 * @returns {{score: number, exactCount: number, startsWithCount: number}}
	 */
	#calculateWeightedScore(item, searchTokens, baseFuzzyScore) {
		const values = this.#extractSearchableValues(item);

		let exactMatchBonus = 0;
		let startsWithBonus = 0;
		let exactCount = 0;
		let startsWithCount = 0;

		for(const token of searchTokens) {
			const upperToken = token.toUpperCase();
			let tokenHasExact = false;
			let tokenHasStartsWith = false;

			for(const valWeight of values) {
				const upperValue = valWeight.value.toUpperCase();

				if(upperValue === upperToken) {
					exactMatchBonus += valWeight.weight;
					tokenHasExact = true;
				} else if(upperValue.startsWith(upperToken)) {
					const coverage = upperToken.length / upperValue.length;
					startsWithBonus += valWeight.weight * coverage;
					tokenHasStartsWith = true;
				}
			}

			if(tokenHasExact) exactCount++;
			else if(tokenHasStartsWith) startsWithCount++;
		}

		// Convert bonuses to score (lower = better match)
		const totalBonus = (exactMatchBonus * 2) + startsWithBonus;
		const finalScore = Math.max(0, baseFuzzyScore - totalBonus);

		return { score: finalScore, exactCount, startsWithCount };
	}

	/**
	 * Extracts all searchable values from an item using the configured keys.
	 * @param {Object} item - The item to extract values from
	 * @returns {Array<{value: string, weight: number}>} Array of value/weight pairs
	 */
	#extractSearchableValues(item) {
		const keys = this.#options.keys || [];
		const values = [];

		if(!keys || !Array.isArray(keys)) {
			return values;
		}

		for(const key of keys) {
			let weight = 1;
			let extractedValues;

			if(typeof key === "string") {
				extractedValues = this.#getNestedValue(item, key);
			} else if(typeof key === "object") {
				weight = key.weight ?? 1;

				if(typeof key.getFn === "function") {
					extractedValues = key.getFn(item);
				} else if(key.name) {
					extractedValues = this.#getNestedValue(item, key.name);
				}
			}

			// Normalize to array
			if(!Array.isArray(extractedValues)) {
				extractedValues = extractedValues != null ? [extractedValues] : [];
			}

			// Add each non-empty string value with its weight
			for(const val of extractedValues) {
				if(val && typeof val === "string") {
					values.push({ value: val, weight });
				}
			}
		}

		return values;
	}

	/**
	 * Gets a nested value from an object using a path string or array.
	 * @param {Object} obj - The object to traverse
	 * @param {string|string[]} path - The path to the value
	 * @returns {*} The nested value or undefined
	 */
	#getNestedValue(obj, path) {
		if(!obj || !path) {
			return undefined;
		}

		const keys = Array.isArray(path) ? path : path.split(".");
		let value = obj;

		for(const key of keys) {
			if(value === null || value === undefined) {
				return undefined;
			}
			value = value[key];
		}

		return value;
	}
}

export default FuzzySearcher;
